// ─── Single source of truth for every in-app route path ─────────────────────
// Every screen path lives here ONCE, as a typed constant. The router, the sidebar
// links, breadcrumbs, redirects and every `navigate(...)` reference `ROUTES.*` —
// never a hand-typed string — so a path changes in one place and flows everywhere,
// and TypeScript flags any stale reference. Paths are real URL paths (History API,
// no `#`), served by the SPA (Caddy falls back to index.html for any of them).
export const ROUTES = {
  home: '/',

  // HQ (cloud)
  overview: '/overview',
  fleetInventory: '/fleet/inventory',
  locations: '/locations',

  // Farm
  iotControl: '/farm/control',
  // ⚠ Production, Traceability, Workforce, AI Monitoring, Room Climate and
  // Climate Incidents were REMOVED 2026-08-30 and their paths went with them.
  // Production PLANNING stays and now carries the name "Production" in the nav;
  // its paths are the `/planning/*` block further down, deliberately unrenamed —
  // a live bookmark is worth more than a tidy URL, and the paths cost nothing.
  cockpit: '/cockpit',
  soc: '/soc',

  // ITSM — every nav leaf owns a path segment mirroring the nav tree
  // (user directive 2026-07-18); record lives UNDER Register (locked pattern:
  // sub-pages under their parent so the leaf stays highlighted).
  assets: '/assets/register',
  assetsStockroom: '/assets/stockroom',
  /** R-235: the append-only movement LEDGER (the prototype's Transfers screen).
   *  Not the transfer-ORDER document — that is a workflow with picking and
   *  reservations, it lives under Maintenance, and conflating the two is how a
   *  ledger ends up with an approval step it should never have. */
  assetsTransfers: '/assets/transfers',
  assetsReceiving: '/assets/receiving',
  assetsRecord: '/assets/register/record',
  /** R-235: the 3-step registration wizard — the manual birth path, beside
   *  goods receipt. Its own page, not a modal: it is two scans and a form, and a
   *  dialog that owns a camera viewfinder is a dialog fighting the screen. */
  assetsIntake: '/assets/intake',
  assetsLabels: '/assets/labels',
  /** R-235: the printable sheet, split out from the pool browser. It takes
   *  `?tags=a,b,c` and owns the @media print styling; the pool is a browsing
   *  screen and a page that hides the app shell is a poor place to browse. */
  assetsLabelsPrint: '/assets/labels/print',
  assetsScan: '/assets/scan',
  /** R-235: the discovered-device quarantine. */
  assetsUnregistered: '/assets/unregistered',
  /** R-235: the taxonomy and the product catalogue, as ASSETS screens rather than
   *  buried in Admin — the prototype's creation order is classes → models → labels
   *  → intake, and burying the first two made that order invisible. */
  assetsClasses: '/assets/classes',
  assetsModels: '/assets/models',
  /** R-244: Maintenance is EXACTLY the prototype's Service section — Incidents and
   *  Changes, nothing else. `grep -oiE "v-(maint|pm|wo|workorder)[a-z]*"` over
   *  sentinel-assets-V8.html returns nothing, so a work-order tab had no
   *  counterpart to be measured against and made the module read as three
   *  co-equal processes when only two of them restore service. */
  maintenance: '/maintenance',
  maintenanceIncidents: '/maintenance/incidents',
  maintenanceChanges: '/maintenance/changes',
  /** R-244 — an incident and a change are PROCESSES, so each record is its own
   *  PAGE (`?id=`), not a drawer that collapses back into the row it came from.
   *  A record with phases, its own actions and a URL somebody can send you is
   *  none of those things inside a table row — and the prototype agrees: its
   *  rows swap the whole view (`go('v-incdetail', …)`) and the record carries a
   *  `← All incidents` way back. */
  maintenanceIncidentRecord: '/maintenance/incidents/record',
  maintenanceChangeRecord: '/maintenance/changes/record',
  /** R-244: work orders are PREVENTIVE and corrective maintenance — the PM
   *  scheduler's output and the technician's queue — not service restoration. They
   *  are their own screen now, one level up, rather than a third Maintenance tab.
   *  The module itself was deliberately NOT deleted: `pm_scheduler`, Technician
   *  Mobile's "My work", the SOC tile, global search's `WO-` resolution and the
   *  asset record's service tab all read it. */
  workOrders: '/work-orders',
  /** @deprecated R-244 — the pre-split path. Kept ONLY so an existing bookmark or
   *  notification deep link still lands somewhere real; it redirects to
   *  {@link ROUTES.workOrders}. Nothing new should point here. */
  maintenanceWorkOrders: '/maintenance/work-orders',
  /** R-235: the transfer-ORDER document — picking, staging, reservations. It moved
   *  here out of Assets, where it had been sitting under the name "Transfers" that
   *  the prototype gives to the movement LEDGER. Change and Work Orders drive it,
   *  which is why Maintenance is where it belongs. */
  maintenanceTransferOrders: '/maintenance/transfer-orders',
  // R-143 — Problem / Known-Error + Knowledge-base lifecycle (cloud tier).
  problems: '/problems',
  knowledgeBase: '/knowledge-base',

  // Commerce
  warehouse: '/warehouse',

  // Procurement (R-234) — its own module, not part of ITSM. The buying chain
  // touches ITSM at exactly two seams: it can order nothing but a catalogue
  // model, and receiving is what turns a purchase order into real assets.
  // Set-up order is Approval Rules FIRST; day-to-day order is the list below.
  // `procurement` stays as the module landing path and redirects to Requests.
  procurement: '/procurement',
  procurementRequests: '/procurement/requests',
  /** Locked sub-page of Requests — takes `?id=`, because the router has no path params. */
  procurementRequestRecord: '/procurement/requests/record',
  procurementApprovals: '/procurement/approvals',
  procurementOrders: '/procurement/orders',
  procurementReceiving: '/procurement/receiving',
  procurementRules: '/procurement/rules',
  logistics: '/logistics',
  logisticsOrder: '/logistics/order',
  logisticsTour: '/logistics/tour',
  sales: '/sales',

  // Administration
  usersRegistration: '/admin/users/registration',
  usersManagement: '/admin/users/management',
  access: '/admin/access',
  sites: '/admin/sites',
  // Master Data keeps the reference tables nobody manages per-crop (climate
  // zones, units, zones). Crops + varieties moved OUT of it into the Cultivation
  // group below — a crop's identity belongs beside its categories and profiles,
  // not beside a units table (R-204).
  masterData: '/admin/master-data',
  // Cultivation is ONE nav leaf over a TABBED page (R-330). The four screens
  // below were four nav LEAVES rendering the same `CultivationPage` with a
  // different `tab` prop — the nav group WAS the tab bar. The paths all stay: a
  // live bookmark is worth more than a tidy URL, and the in-page strip navigates
  // between them, so the URL remains the only source of truth for which tab is
  // open (no second copy of it in component state to drift).
  //
  // `cultivation` is the base path, and it is NOT a route of its own. It is (a)
  // the nav leaf's KEY — every tab path below matches it by prefix, which is what
  // keeps the leaf highlighted on all of them (`matches()` in nav-tree.tsx) — and
  // (b) the fold-forward target in Router.legacyTarget.
  cultivation: '/admin/cultivation',
  //
  // R-210: Crops, Varieties and Categories were three leaves over ONE hierarchy
  // (`crop_categories → crops → crop_varieties`) — an artefact of R-204 reusing
  // the Master Data tables rather than a decision about the domain. They are now
  // one Catalogue tree; all three retired paths fold forward to it.
  cultivationCatalogue: '/admin/cultivation/catalogue',
  // ⚠ R-315: cultivation models are GONE. What this leaf edits now is the farm's
  // ONE stage vocabulary — the models became data (R-251), then generic (R-314),
  // then were removed entirely. Old path: /admin/cultivation/models.
  // (R-251 note kept for the trail: cultivation models became DATA (they were a hardcoded five-value
  // enum), so the stage machine a crop follows is now something an operator
  // defines. Its own leaf, before Catalogue in the nav, because a crop cannot be
  // created without choosing one.
  cultivationStages: '/admin/cultivation/stages',
  cultivationProfiles: '/admin/cultivation/profiles',
  cultivationFlags: '/admin/cultivation/flags',
  cultivationImport: '/admin/cultivation/import',
  // R-320 — production planning is a nav GROUP under Farm, one leaf per screen.
  // Capacity sits FIRST because nothing can be planned until a room has a rack
  // geometry and a crop has a yield; the order of the leaves is the order of
  // the set-up.
  planningCapacity: '/planning/capacity',
  planningPlans: '/planning/plans',
  /** Locked sub-page of Plans — takes `?id=`, because the router has no path params. */
  planningPlanRecord: '/planning/plans/record',
  /** R-353 — what the crops themselves reported: every `cultivation.*` alert the
   *  farm raised, site-wide. Sits beside Plans because it is the same subject
   *  read the other way round: a plan says what SHOULD happen, this says what
   *  did. Takes `?plan=` / `?room=` to arrive pre-filtered from a deep link. */
  planningAlerts: '/planning/alerts',
  planningTimeline: '/planning/timeline',
  planningTasks: '/planning/tasks',
  itsmCommons: '/admin/itsm-commons',
  deviceCatalog: '/admin/device-catalog',
  audit: '/admin/audit',

  // History
  historyMicrocontrollers: '/history/devices/microcontrollers',
  historySignals: '/history/devices/signals',

  // Setup
  mapper: '/farm/mapper',
  firmware: '/farm/firmware',
  // Aiming an image is its own PAGE, not a dialog: the list is sized by the farm
  // (~280 boards at 14 rooms), and a full width screen survives a reload.
  firmwarePush: '/farm/firmware/push',
  devicesMicrocontrollers: '/farm/devices/microcontrollers',
  devicesSensors: '/farm/devices/sensors',
  // R-333. A leaf of its own rather than a tab on Sensors: it lists PROBES
  // (mac × channel), not readings by place, and it shows channels the Sensors
  // screen structurally cannot — a board on a rack LEVEL, and a board mapped
  // nowhere at all.
  devicesCalibration: '/farm/devices/calibration',
  devicesActuators: '/farm/devices/actuators',

  // Technician mobile (R-147 W6) — the dedicated `/m/*` layer rendered inside
  // the MobileShellLayout (bottom nav). Task detail = mWork + `?kind=&id=`.
  mHome: '/m',
  mWork: '/m/work',
  mScan: '/m/scan',
  mProfile: '/m/profile',

  // Account
  settings: '/settings',
  notifications: '/notifications',

  // Public (no auth)
  completeRegistration: '/complete-registration',
  recover: '/recover',
  mfaRecover: '/mfa-recover',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = (typeof ROUTES)[RouteKey];

/** Append a query string to a route path (drops empty/undefined values). Keeps the SSOT path intact:
 *  `href(ROUTES.logisticsOrder, { id })`. */
export function href(path: RoutePath, params?: Record<string, string | number | null | undefined>): string {
  if (!params) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}
